import defaultAvatar from '@/assets/images/default-avatar.jpg';
import { Button } from '@/components/Button';
import { DropdownMenu } from '@/components/Dropdown/DropdownMenu';
import { DropdownMenuItem } from '@/components/Dropdown/DropdownMenuItem';
import { Image } from '@/components/Image';
import { Modal } from '@/components/Modal';
import { SelectInput } from '@/components/SelectInput';
import { Text } from '@/components/Text';
import { useEventStore } from '@/stores/useEventStore';
import { useUserStore } from '@/stores/useUserStore';
import { EventUser } from '@/types/event';
import { User } from '@/types/user';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEllipsisVertical, FaUser } from 'react-icons/fa6';
import { IoMdPersonAdd } from 'react-icons/io';
import { MdDelete } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';

// Animations variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, when: 'beforeChildren' },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 24 },
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } },
};

// EventUserCard component
const EventUserCard = ({
  user,
  onEdit,
  onRemove,
}: {
  user: EventUser;
  onEdit: (user: EventUser) => void;
  onRemove: (user: EventUser) => void;
}) => {
  return (
    <motion.div
      variants={itemVariants}
      className="flex h-48 flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
    >
      {/* Header avec avatar et nom */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <Image
            src={defaultAvatar}
            alt={`${user.firstname} ${user.lastname}`}
            className="size-12 rounded-full object-cover"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-gray-900">
            {user.firstname} {user.lastname}
          </h3>
          <p className="truncate text-xs text-gray-500">{user.email}</p>
        </div>
        <DropdownMenu
          trigger={
            <button className="rounded p-1 hover:bg-gray-100">
              <FaEllipsisVertical className="size-4 text-gray-400" />
            </button>
          }
        >
          <DropdownMenuItem
            onClick={() => onRemove(user)}
            className="flex w-full items-center gap-2 text-left text-red-500"
          >
            <MdDelete className="size-4" />
            Supprimer
          </DropdownMenuItem>
        </DropdownMenu>
      </div>

      {/* Informations supplémentaires */}
      <div className="mt-3 flex-1 space-y-2">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span className="font-medium">ID:</span>
          <span>#{user.id}</span>
        </div>
        {user.phone && (
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className="font-medium">Téléphone:</span>
            <span className="truncate">{user.phone}</span>
          </div>
        )}
      </div>

      {/* Action principale */}
      <button
        onClick={() => onEdit(user)}
        className="mt-auto w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
      >
        Voir le profil
      </button>
    </motion.div>
  );
};

export const UpdateEventUsersList = () => {
  const { t, i18n } = useTranslation();
  const {
    users,
    fetchEventUsers,
    filters,
    loading,
    addEventUser,
    removeEventUser,
  } = useEventStore();
  const { users: allUsers, getUsers } = useUserStore();
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      await fetchEventUsers(Number(id), {
        paginate: 0,
        type: 'all',
        search: filters.search,
      });
    };

    fetchUsers();
  }, [id, fetchEventUsers, filters.search]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      await getUsers();
    };
    fetchAllUsers();
  }, [getUsers]);

  const handleEdit = (user: EventUser) => {
    navigate(`/${i18n.language}/dashboard/users/${user.id}`);
  };

  const handleAddUser = () => {
    setIsModalOpen(true);
  };

  const handleAddUserSubmit = async () => {
    if (selectedUser) {
      await addEventUser(Number(id), selectedUser.id);
      setIsModalOpen(false);
      setSelectedUser(null);
    }
  };

  const handleRemoveUser = async (user: EventUser) => {
    await removeEventUser(Number(id), user.id);
  };

  return (
    <div className="h-full space-y-6 overflow-y-auto">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="space-y-6"
      >
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-secondary">
              {t('updateEvent:users.title')}
            </h1>
            <Text className="text-sm font-light text-gray-light">
              ({filters.total || 0})
            </Text>
          </div>
          <Button
            onClick={handleAddUser}
            className="flex w-full items-center justify-center gap-2 lg:w-auto"
          >
            <IoMdPersonAdd className="size-4" />
            {t('updateEvent:users.add')}
          </Button>
        </div>
      </motion.div>

      {/* Liste des utilisateurs avec InfiniteScrollList */}
      {users.length === 0 && !loading ? (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="flex flex-col items-center justify-center px-4 pb-4"
        >
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex size-20 items-center justify-center rounded-full bg-gray-100">
              <FaUser className="size-10 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              Aucun utilisateur
            </h3>
            <p className="mb-6 max-w-sm text-sm text-gray-500">
              Aucun utilisateur n&apos;est inscrit à cet événement pour le
              moment.
            </p>
            <Button
              onClick={handleAddUser}
              className="bg-linko-light hover:bg-linko-dark"
            >
              {t('updateEvent:users.add')}
            </Button>
          </div>
        </motion.div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence>
              {users.map(user => (
                <motion.div key={user.id} variants={itemVariants}>
                  <EventUserCard
                    user={user}
                    onEdit={handleEdit}
                    onRemove={handleRemoveUser}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      )}

      {/* Modal d'ajout d'utilisateur */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedUser(null);
          }}
          title={t('updateEvent:users.add')}
        >
          <div className="flex flex-col gap-4">
            <SelectInput
              name="user_id"
              defaultValue={selectedUser?.id ?? null}
              options={allUsers.map(user => ({
                label: `${user.firstname} ${user.lastname}`,
                value: user.id,
              }))}
              placeholder={t('updateEvent:users.select.placeholder')}
              useFormik={false}
              menuPortalTarget={document.body}
              onChange={event => {
                setSelectedUser(
                  allUsers.find(user => user.id === event.target.value) ?? null,
                );
              }}
            />
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedUser(null);
                }}
                className="w-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 sm:w-auto"
              >
                {t('common:cancel')}
              </Button>
              <Button
                onClick={handleAddUserSubmit}
                disabled={!selectedUser}
                className="w-full sm:w-auto"
              >
                {t('updateEvent:users.add')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
